if (!customElements.get('media-gallery')) {
  customElements.define(
    'media-gallery',
    class MediaGallery extends HTMLElement {
      constructor() {
        super();
        this.mql = window.matchMedia('(min-width: 750px)');
        this.boundOnSlideChanged = this.onSlideChanged.bind(this);
        this.boundOnThumbnailClick = this.onThumbnailClick.bind(this);
        this.boundOnGalleryArrowClick = this.onGalleryArrowClick.bind(this);

        // The <media-gallery> host survives Shopify variant HTML refreshes,
        // so keep the custom arrow handler here instead of on replaceable buttons.
        this.addEventListener('click', this.boundOnGalleryArrowClick, true);

        this.refresh();
      }

      refresh() {
        this.elements = {
          liveRegion: this.querySelector('[id^="GalleryStatus"]'),
          viewer: this.querySelector('[id^="GalleryViewer"]'),
          thumbnails: this.querySelector('[id^="GalleryThumbnails"]'),
        };

        if (this.elements.viewer && !this.elements.viewer.dataset.ffGalleryListener) {
          this.elements.viewer.addEventListener('slideChanged', this.boundOnSlideChanged);
          this.elements.viewer.dataset.ffGalleryListener = 'true';
        }

        if (this.elements.thumbnails && !this.elements.thumbnails.dataset.ffGalleryListener) {
          this.elements.thumbnails.addEventListener('click', this.boundOnThumbnailClick);
          this.elements.thumbnails.dataset.ffGalleryListener = 'true';
        }

        // Shopify's generic slider disables arrows according to whether the
        // first/last thumbnail is visible. This gallery intentionally shows
        // several thumbnails at once, so that rule is wrong for photo navigation.
        if (this.elements.thumbnails) {
          this.elements.thumbnails.enableSliderLooping = true;
        }

        this.elements.viewer?.resetPages?.();
        this.elements.thumbnails?.resetPages?.();

        const activeMedia =
          this.elements.viewer?.querySelector('.product__media-item.is-active') ||
          this.elements.viewer?.querySelector('[data-media-id]');

        if (activeMedia) {
          const activeThumbnail = this.elements.thumbnails?.querySelector(`[data-target="${activeMedia.dataset.mediaId}"]`);
          this.setActiveThumbnail(activeThumbnail);
        }

        this.updateThumbnailArrows();

        if (this.dataset.desktopLayout?.includes('thumbnail') && this.mql.matches) this.removeListSemantic();
      }

      getThumbnailItems() {
        return Array.from(
          this.elements.thumbnails?.querySelectorAll('.thumbnail-list__item[data-target]') || []
        );
      }

      getActiveThumbnailIndex(items = this.getThumbnailItems()) {
        if (!items.length) return -1;

        const currentButton = this.elements.thumbnails?.querySelector('.thumbnail[aria-current="true"]');
        const currentItem = currentButton?.closest('.thumbnail-list__item[data-target]');

        if (currentItem) {
          const currentIndex = items.indexOf(currentItem);
          if (currentIndex > -1) return currentIndex;
        }

        const activeMedia = this.elements.viewer?.querySelector('.product__media-item.is-active');
        const activeMediaId = activeMedia?.dataset?.mediaId;

        if (activeMediaId) {
          const matchedIndex = items.findIndex((item) => item.dataset.target === activeMediaId);
          if (matchedIndex > -1) return matchedIndex;
        }

        return 0;
      }

      setThumbnailArrowState(button, disabled) {
        if (!button) return;

        button.classList.toggle('is-disabled', disabled);
        button.disabled = disabled;
        button.setAttribute('aria-disabled', disabled ? 'true' : 'false');

        if (disabled) {
          button.setAttribute('disabled', 'disabled');
        } else {
          button.removeAttribute('disabled');
        }
      }

      updateThumbnailArrows() {
        const thumbnails = this.elements.thumbnails;
        if (!thumbnails) return;

        // Keep Shopify's visibility-based arrow disabling turned off after
        // every variant refresh / slider reset.
        thumbnails.enableSliderLooping = true;

        const prev = thumbnails.querySelector('.slider-button--prev');
        const next = thumbnails.querySelector('.slider-button--next');
        if (!prev || !next) return;

        const items = this.getThumbnailItems();

        if (items.length <= 1) {
          this.setThumbnailArrowState(prev, true);
          this.setThumbnailArrowState(next, true);
          return;
        }

        const currentIndex = this.getActiveThumbnailIndex(items);
        this.setThumbnailArrowState(prev, currentIndex <= 0);
        this.setThumbnailArrowState(next, currentIndex >= items.length - 1);
      }

      onGalleryArrowClick(event) {
        const button = event.target.closest('.slider-button--prev, .slider-button--next');

        if (!button || !this.elements.thumbnails?.contains(button)) return;

        // Run before SliderComponent's own button handler.
        event.preventDefault();
        event.stopPropagation();
        event.stopImmediatePropagation();

        if (button.disabled) return;

        const items = this.getThumbnailItems();
        if (items.length <= 1) {
          this.updateThumbnailArrows();
          return;
        }

        const currentIndex = this.getActiveThumbnailIndex(items);
        const direction = button.classList.contains('slider-button--prev') ? -1 : 1;
        const targetIndex = Math.max(0, Math.min(items.length - 1, currentIndex + direction));

        if (targetIndex === currentIndex) {
          this.updateThumbnailArrows();
          return;
        }

        const targetMediaId = items[targetIndex]?.dataset?.target;
        if (!targetMediaId) return;

        this.setActiveMedia(targetMediaId);
      }

      onThumbnailClick(event) {
  const thumbnailItem = event.target.closest('[data-target]');

  if (!thumbnailItem || !this.elements.thumbnails?.contains(thumbnailItem)) return;

  event.preventDefault();
  event.stopPropagation();

  this.setActiveMedia(thumbnailItem.dataset.target, false);
}

      onSlideChanged(event) {
  const currentElement = event.detail?.currentElement;

  if (!currentElement) return;

  this.elements.viewer?.querySelectorAll('[data-media-id]').forEach((element) => {
    element.classList.remove('is-active');
  });

  currentElement.classList.add('is-active');

  const thumbnail = this.elements.thumbnails?.querySelector(
    `[data-target="${currentElement.dataset.mediaId}"]`
  );

  this.setActiveThumbnail(thumbnail);
  this.playActiveMedia(currentElement);
}

      setActiveMedia(mediaId) {
        this.refreshElementsOnly();

        const activeMedia =
          this.elements.viewer?.querySelector(`[data-media-id="${mediaId}"]`) ||
          this.elements.viewer?.querySelector('[data-media-id]');

        if (!activeMedia) return;

        this.elements.viewer.querySelectorAll('[data-media-id]').forEach((element) => {
          element.classList.remove('is-active');
        });
        activeMedia.classList.add('is-active');

        this.preventStickyHeader();

        window.setTimeout(() => {
          const slider = activeMedia.parentElement;
          if (slider && typeof activeMedia.offsetLeft === 'number') {
            slider.scrollTo({ left: activeMedia.offsetLeft, behavior: 'smooth' });
          }
        });

        this.playActiveMedia(activeMedia);

        const activeThumbnail = this.elements.thumbnails?.querySelector(`[data-target="${mediaId}"]`);
        this.setActiveThumbnail(activeThumbnail);

        if (activeThumbnail) {
          this.announceLiveRegion(activeMedia, activeThumbnail.dataset.mediaPosition);
        }
      }

      refreshElementsOnly() {
        this.elements = {
          liveRegion: this.querySelector('[id^="GalleryStatus"]'),
          viewer: this.querySelector('[id^="GalleryViewer"]'),
          thumbnails: this.querySelector('[id^="GalleryThumbnails"]'),
        };
      }

      setActiveThumbnail(thumbnail) {
        if (!this.elements.thumbnails || !thumbnail) return;

        this.elements.thumbnails
          .querySelectorAll('button')
          .forEach((element) => element.removeAttribute('aria-current'));

        const thumbnailButton = thumbnail.querySelector('button');
        if (!thumbnailButton) return;

        thumbnailButton.setAttribute('aria-current', true);
        this.updateThumbnailArrows();

        if (this.elements.thumbnails.isSlideVisible?.(thumbnail, 10)) return;
        this.elements.thumbnails.slider?.scrollTo({ left: thumbnail.offsetLeft, behavior: 'smooth' });
      }

      announceLiveRegion(activeItem, position) {
        const image = activeItem.querySelector('.product__modal-opener--image img');
        if (!image || !this.elements.liveRegion) return;

        image.onload = () => {
          this.elements.liveRegion.setAttribute('aria-hidden', false);
          this.elements.liveRegion.innerHTML = window.accessibilityStrings.imageAvailable.replace('[index]', position);
          setTimeout(() => {
            this.elements.liveRegion.setAttribute('aria-hidden', true);
          }, 2000);
        };
        image.src = image.src;
      }

      announceLiveRegion(activeItem, position) {
        const image = activeItem.querySelector('.product__modal-opener--image img');
        if (!image) return;
        image.onload = () => {
          this.elements.liveRegion.setAttribute('aria-hidden', false);
          this.elements.liveRegion.innerHTML = window.accessibilityStrings.imageAvailable.replace('[index]', position);
          setTimeout(() => {
            this.elements.liveRegion.setAttribute('aria-hidden', true);
          }, 2000);
        };
        image.src = image.src;
      }

      playActiveMedia(activeItem) {
  if (!activeItem) return;

  window.pauseAllMedia();

  const deferredMedia = activeItem.querySelector('.deferred-media');
  if (!deferredMedia) return;

  function startLoadedMedia() {
    const video = deferredMedia.querySelector('video');
    const iframe = deferredMedia.querySelector('iframe');

    if (video) {
      video.muted = true;
      video.setAttribute('muted', 'muted');
      video.setAttribute('playsinline', 'playsinline');
      video.setAttribute('webkit-playsinline', 'webkit-playsinline');

      const playPromise = video.play();

      if (playPromise && typeof playPromise.catch === 'function') {
        playPromise.catch(function () {});
      }
    }

    if (iframe && iframe.contentWindow) {
      if (iframe.classList.contains('js-youtube')) {
        iframe.contentWindow.postMessage(
          '{"event":"command","func":"playVideo","args":""}',
          '*'
        );
      }

      if (iframe.classList.contains('js-vimeo')) {
        iframe.contentWindow.postMessage(
          '{"method":"play"}',
          '*'
        );
      }
    }
  }

  if (!deferredMedia.getAttribute('loaded')) {
    deferredMedia.loadContent(false);

    requestAnimationFrame(startLoadedMedia);
    setTimeout(startLoadedMedia, 150);
    setTimeout(startLoadedMedia, 500);
    setTimeout(startLoadedMedia, 900);
  } else {
    startLoadedMedia();
  }
}

      preventStickyHeader() {
        this.stickyHeader = this.stickyHeader || document.querySelector('sticky-header');
        if (!this.stickyHeader) return;
        this.stickyHeader.dispatchEvent(new Event('preventHeaderReveal'));
      }

      removeListSemantic() {
        if (!this.elements.viewer?.slider) return;
        this.elements.viewer.slider.setAttribute('role', 'presentation');
        this.elements.viewer.sliderItems.forEach((slide) => slide.setAttribute('role', 'presentation'));
      }
    }
  );
}
